import React from 'react';
import ActionsMenu, {
    ActionsMenuProps,
} from '../../../common/Navigation/ActionsMenu';
import CcNode, {
    CcNodeBase,
    CcNodeRole,
    CcNodeStatus,
    CcNodeType,
} from '../../../services/models/CcNode';
import CcCluster, {
    CcClusterBase,
    CcClusterType,
} from '../../../services/models/CcCluster';
import CcMySqlNode from '../../../services/models/CcMySqlNode';
import CcPostgreSqlNode from '../../../services/models/CcPostgreSqlNode';
import NodeRebootHostButton from './NodeRebootHostButton';
import NodeDisableBinaryLoggingButton from './NodeDisableBinaryLoggingButton';
import NodeStopButton from './NodeStopButton';
import NodeRestartButton from './NodeRestartButton';
import NodeStartButton from './NodeStartButton';
import NodeUnregisterButton from './NodeUnregisterButton';
import NodeChangeReplicationMasterButton from './NodeChangeReplicationMasterButton';
import NodePromoteSlaveButton from './NodePromoteSlaveButton';
import NodeStopSlaveButton from './NodeStopSlaveButton';
import NodeStartSlaveButton from './NodeStartSlaveButton';
import NodeToggleReadonlyButton from './NodeToggleReadonlyButton';
import AddMaintenanceButton from '../../Maintenance/AddMaintenanceButton';
import DisableMaintenanceButton from '../../Maintenance/DisableMaintenanceButton';
import NodeResetReplicaButton from './NodeResetReplicaButton';
import NodeRebuildReplicationSlaveButton from './NodeRebuildReplicationSlaveButton';
import ClusterConfigureWalButton from '../../Clusters/Actions/ClusterConfigureWalButton';
import useMaintenanceWatcher from '../../Maintenance/useMaintenanceWatcher';
import ManageMaintenanceButton from '../../Maintenance/ManageMaintenanceButton';
import NodeToggleSynchronousReplicationButton from './NodeToggleSynchronousReplicationButton';
import NodeFreezeButton from './NodeFreezeButton';
import NodeStepDownButton from './NodeStepDownButton';
import CcMongoNode, {
    CcMongoNodeMemberRole,
} from '../../../services/models/CcMongoNode';
import { ActionMenuItem } from '@severalnines/bar-frontend-components/build/lib/Navigation/ActionMenu';
import NodeDetailsButton from './NodeDetailsButton';
import ProxySqlExportConfigButton from '../ProxySqlAction/ProxySqlExportConfigButton';
import ProxySqlSynchronizeInstancesButton from '../ProxySqlAction/ProxySqlSynchronizeInstancesButton';
import {
    BuildOutlined,
    ClusterOutlined,
    CodeOutlined,
    DeleteOutlined,
    DeploymentUnitOutlined,
    ExportOutlined,
    FileSearchOutlined,
    ForkOutlined,
    NodeCollapseOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SaveOutlined,
    StopOutlined,
    ToolOutlined,
    ToTopOutlined,
} from '@ant-design/icons';
import AppDivider from '../../../common/AppDivider';
import {
    addDividerToMenuGroup,
    groupSimilarMenuItemsInSubmenu,
} from '../../../common/Navigation/actionMenuHelpers';
import NodeResyncButton from './NodeResyncButton';
import ClusterBootstrapButton from '../../Clusters/Actions/ClusterBootstrapButton';
import NodeSshConsoleButton from './NodeSshConsoleButton';
import NodeEnableBinaryLoggingButton from './NodeEnableBinaryLoggingButton';

export default NodeActionsMenu;

export type NodeActionsMenuProps = ActionsMenuProps & {
    node: CcNode;
    cluster: CcCluster;
    onActionPerformed?: () => void;
};

function NodeActionsMenu({
    node,
    cluster,
    onActionPerformed,
    ...rest
}: NodeActionsMenuProps) {
    const {
        active: activeMaintenance,
        next: nextMaintenance,
    } = useMaintenanceWatcher({
        hostname: node.hostname,
    });

    const handleActionSuccess = () => {
        onActionPerformed?.();
    };
    let nodeDetailsButton = undefined;
    let nodeDisableBinaryLogButton = undefined;
    let nodeEnableBinaryLogButton = undefined;
    let nodeToggleReadonlyButton = undefined;
    let nodePromoteSlaveButton = undefined;
    let nodeRebuildReplicationSlaveButton = undefined;
    let nodeChangeReplicationMasterButton = undefined;
    let nodeStopSlaveButton = undefined;
    let nodeStartSlaveButton = undefined;
    let nodeResetReplicaButton = undefined;
    let nodeSshConsoleButton = undefined;

    if (
        [
            CcClusterType.TYPE_REPLICATION,
            CcClusterType.TYPE_GALERA,
            CcClusterType.TYPE_POSTGRESQL,
            CcClusterType.TYPE_TIMESCALEDB,
        ].includes(cluster.clusterType) &&
        node.isPerformanceHaNode()
    ) {
        nodeDetailsButton = {
            key: 'node-details-button',
            icon: <FileSearchOutlined />,
            label: <NodeDetailsButton node={node} cluster={cluster} />,
        };
    }

    if (
        [
            CcClusterType.TYPE_GALERA,
            CcClusterType.TYPE_REPLICATION,
            CcClusterType.TYPE_MSSQL_AO_ASYNC,
            CcClusterType.TYPE_POSTGRESQL,
            CcClusterType.TYPE_TIMESCALEDB,
        ].includes(cluster.clusterType)
    ) {
        if (
            // is Mysql, Mssql or Postgres and replica, readonly or failed primary
            ([
                CcNodeType.MYSQL,
                CcNodeType.MSSQL,
                CcNodeType.POSTGRESQL,
                CcNodeType.TIMESCALEDB,
            ].includes(node.getType()) &&
                (node.readonly ||
                    node.isReplica() ||
                    node.isRole(CcNodeRole.FAILED_PRIMARY))) ||
            // or is postgres and primary not connected or shutdown
            (node.isBase(CcNodeBase.BASE_POSTGRESQL) &&
                node.isPrimary() &&
                (!node.connected ||
                    (node.hoststatus as CcNodeStatus) ===
                        CcNodeStatus.CmonHostShutDown))
        ) {
            nodeRebuildReplicationSlaveButton = {
                key: 'node-rebuild-replication-slave',
                icon: <BuildOutlined />,
                label: (
                    <NodeRebuildReplicationSlaveButton
                        node={node}
                        cluster={cluster}
                    />
                ),
            };
        }
    }

    if (
        [CcNodeStatus.CmonHostOnline, CcNodeStatus.CmonHostFailed].includes(
            node.hoststatus as CcNodeStatus
        )
    ) {
        if (
            node.isType(CcNodeType.GALERA) &&
            node.isRole(CcNodeRole.MASTER) &&
            node instanceof CcMySqlNode &&
            node.isBinaryLogON()
        ) {
            nodeDisableBinaryLogButton = {
                key: 'node-disable-binary-logging',
                icon: <span></span>,
                label: <NodeDisableBinaryLoggingButton node={node} />,
            };
        }

        if (
            node.isType(CcNodeType.GALERA) &&
            node instanceof CcMySqlNode &&
            !node.isBinaryLogON()
        ) {
            nodeEnableBinaryLogButton = {
                key: 'node-disable-binary-logging',
                icon: <span></span>,
                label: <NodeEnableBinaryLoggingButton node={node} />,
            };
        }

        if (
            (node.isType(CcNodeType.MYSQL) || node.isType(CcNodeType.GALERA)) &&
            node.connected &&
            !(
                cluster.isType(CcClusterType.TYPE_REPLICATION) &&
                cluster.getDatabaseNodes().length === 1
            )
        ) {
            nodeToggleReadonlyButton = {
                key: 'node-enable-readonly',
                waitForConfirm: true,
                icon: <span></span>,
                label: <NodeToggleReadonlyButton node={node} />,
            };
        }

        const mysqlReplicaNode =
            (cluster.isType(CcClusterType.TYPE_MYSQL_SINGLE) ||
                cluster.isType(CcClusterType.TYPE_REPLICATION)) &&
            node.readonly &&
            node.isType(CcNodeType.MYSQL) &&
            node.isReplica();
        const postgresReplicaNode =
            cluster.isBase(CcClusterBase.BASE_POSTGRESQL) &&
            node.isBase(CcNodeBase.BASE_POSTGRESQL) &&
            node.isReplica();
        const mssqlReplicaNode =
            cluster.isType(CcClusterType.TYPE_MSSQL_AO_ASYNC) &&
            node.isType(CcNodeType.MSSQL) &&
            node.isReplica();
        if (mysqlReplicaNode || postgresReplicaNode || mssqlReplicaNode) {
            nodePromoteSlaveButton = {
                key: 'node-promote-slave',
                icon: <ToTopOutlined />,
                label: (
                    <NodePromoteSlaveButton
                        node={node}
                        clusterType={cluster.clusterType}
                    />
                ),
            };
        }

        if (
            (cluster.isType(CcClusterType.TYPE_GALERA) &&
                node.isRole(CcNodeRole.SLAVE)) ||
            (cluster.isType(CcClusterType.TYPE_REPLICATION) &&
                node.isType(CcNodeType.MYSQL))
        ) {
            nodeChangeReplicationMasterButton = {
                key: 'node-change-replication-master',
                icon: <NodeCollapseOutlined />,
                label: (
                    <NodeChangeReplicationMasterButton
                        node={node}
                        cluster={cluster}
                    />
                ),
            };
        }

        if (
            (node.isType(CcNodeType.GALERA) || node.isType(CcNodeType.MYSQL)) &&
            node.isReplica()
        ) {
            nodeResetReplicaButton = {
                key: 'node-reset-replica',
                waitForConfirm: true,
                icon: <ReloadOutlined />,
                label: <NodeResetReplicaButton node={node} />,
            };

            if ((node as CcMySqlNode).isReplicationRunning()) {
                nodeStopSlaveButton = {
                    key: 'node-stop-slave',
                    waitForConfirm: true,
                    icon: <StopOutlined />,
                    label: <NodeStopSlaveButton node={node} />,
                };
            }

            if (
                !(node as CcMySqlNode).isReplicationIoRunning() &&
                !(node as CcMySqlNode).isReplicationSqlRunning()
            ) {
                nodeStartSlaveButton = {
                    key: 'node-start-slave',
                    icon: <PlayCircleOutlined />,
                    waitForConfirm: true,
                    label: <NodeStartSlaveButton node={node} />,
                };
            }
            if (
                (node as CcMySqlNode).isReplicationIoRunning() &&
                !(node as CcMySqlNode).isReplicationSqlRunning()
            ) {
                nodeStartSlaveButton = {
                    key: 'node-start-slave-sql-thread',
                    waitForConfirm: true,
                    icon: <PlayCircleOutlined />,
                    label: (
                        <NodeStartSlaveButton
                            node={node}
                            title="Start replica SQL thread"
                        />
                    ),
                };
                nodeStopSlaveButton = {
                    key: 'node-stop-slave-io-thread',
                    waitForConfirm: true,
                    icon: <StopOutlined />,
                    label: (
                        <NodeStopSlaveButton
                            node={node}
                            title="Stop replica IO thread"
                        />
                    ),
                };
            }
            if (
                !(node as CcMySqlNode).isReplicationIoRunning() &&
                (node as CcMySqlNode).isReplicationSqlRunning()
            ) {
                nodeStartSlaveButton = {
                    key: 'node-start-slave-io-thread',
                    waitForConfirm: true,
                    icon: <PlayCircleOutlined />,
                    label: (
                        <NodeStartSlaveButton
                            node={node}
                            title="Start replica IO thread"
                        />
                    ),
                };
                nodeStopSlaveButton = {
                    key: 'node-stop-slave-sql-thread',
                    waitForConfirm: true,
                    icon: <StopOutlined />,
                    label: (
                        <NodeStopSlaveButton
                            node={node}
                            title="Stop replica SQL thread"
                        />
                    ),
                };
            }
        }
    }

    let nodeRebootHostButton = {
        key: 'node-reboot-host',
        waitForConfirm: true,
        icon: <ReloadOutlined />,
        critical: true,
        label: <NodeRebootHostButton node={node} />,
    };
    let nodeRestartButton = undefined;
    let nodeStopButton = undefined;
    if (
        [CcNodeStatus.CmonHostOnline, CcNodeStatus.CmonHostFailed].includes(
            node.hoststatus as CcNodeStatus
        )
    ) {
        nodeRestartButton = {
            key: 'node-restart-node',
            icon: <ReloadOutlined />,
            critical: true,
            label: <NodeRestartButton node={node} />,
        };
        nodeStopButton = {
            key: 'node-stop-node',
            icon: <StopOutlined />,
            critical: true,
            label: <NodeStopButton node={node} />,
        };
    }

    const nodeResyncButton = node.isType(CcNodeType.GALERA)
        ? {
              key: 'node-resync-node',
              icon: <ReloadOutlined />,
              label: <NodeResyncButton node={node} />,
          }
        : undefined;

    const bootstrapCluster = node.isType(CcNodeType.GALERA)
        ? {
              key: 'bootstrap-cluster',
              icon: <ClusterOutlined />,
              critical: true,
              label: <ClusterBootstrapButton node={node} cluster={cluster} />,
          }
        : undefined;

    const nodeStartButton = [
        CcNodeStatus.CmonHostShutDown,
        CcNodeStatus.CmonHostOffLine,
    ].includes(node.hoststatus as CcNodeStatus)
        ? {
              key: 'node-start-node',
              icon: <PlayCircleOutlined />,
              label: <NodeStartButton node={node} />,
          }
        : undefined;

    const nodeUnregisterButton = {
        key: 'node-unregister-node',
        icon: <DeleteOutlined />,
        critical: true,
        label: <NodeUnregisterButton node={node} cluster={cluster} />,
    };

    const nodeEnableMaintenanceModeButton = {
        key: 'node-enable-maintenance-mode',
        label: <AddMaintenanceButton node={node} />,
    };
    const nodeManageMaintenanceModeButton =
        nextMaintenance || activeMaintenance
            ? {
                  key: 'node-manage-maintenance-mode',
                  label: (
                      <ManageMaintenanceButton
                          node={node}
                          onSuccess={handleActionSuccess}
                      />
                  ),
              }
            : undefined;
    const nodeDisableMaintenanceModeButton = node.isMaintenanceModeEnabled()
        ? {
              key: 'node-disable-maintenance-mode',
              waitForConfirm: true,
              label: (
                  <DisableMaintenanceButton
                      node={node}
                      onSuccess={handleActionSuccess}
                  />
              ),
          }
        : undefined;

    // proxysql
    const nodeProxySqlExportConfigButton = node.isType(CcNodeType.PROXYSQL)
        ? {
              key: 'proxysql-export-config',
              icon: <ExportOutlined />,
              label: (
                  <ProxySqlExportConfigButton
                      cluster={cluster}
                      onSuccess={handleActionSuccess}
                  />
              ),
          }
        : null;
    const nodeProxySqlSynchronizeInstancesButton = node.isType(
        CcNodeType.PROXYSQL
    )
        ? {
              key: 'proxysql-synchronize-instances',
              icon: <DeploymentUnitOutlined />,
              label: (
                  <ProxySqlSynchronizeInstancesButton
                      cluster={cluster}
                      onSuccess={handleActionSuccess}
                  />
              ),
          }
        : null;

    // postgres
    const nodeConfigureWalButton =
        cluster.isBase(CcClusterBase.BASE_POSTGRESQL) &&
        node.isBase(CcNodeBase.BASE_POSTGRESQL) &&
        node.connected
            ? {
                  key: 'node-configure-wal',
                  icon: <SaveOutlined />,
                  label: (
                      <ClusterConfigureWalButton
                          cluster={cluster}
                          node={node}
                          onSuccess={handleActionSuccess}
                      />
                  ),
              }
            : null;

    const nodeToggleSynchronousReplicationButton =
        cluster.isBase(CcClusterBase.BASE_POSTGRESQL) &&
        node.isType([CcNodeType.POSTGRESQL, CcNodeType.TIMESCALEDB]) &&
        node.isReplica()
            ? {
                  key: 'node-toggle-synchronous-replication',
                  waitForConfirm: true,
                  label: (
                      <NodeToggleSynchronousReplicationButton
                          node={node as CcPostgreSqlNode}
                          onSuccess={handleActionSuccess}
                      />
                  ),
              }
            : null;

    // mongo
    const isMongoNode =
        cluster.isBase(CcClusterBase.BASE_MONGODB) &&
        node.isType(CcNodeType.MONGO);
    const nodeFreezeButton =
        isMongoNode &&
        !node.isRole(CcNodeRole.MONGO_MONGOS) &&
        !node.isRole(CcNodeRole.MONGO_CONFIG_SERVER)
            ? {
                  key: 'node-freeze',
                  icon: <PauseCircleOutlined />,
                  label: (
                      <NodeFreezeButton
                          node={node as CcMongoNode}
                          onSuccess={handleActionSuccess}
                      />
                  ),
              }
            : null;

    const nodeStepDownButton =
        isMongoNode &&
        (node as CcMongoNode).memberRole === CcMongoNodeMemberRole.PRIMARY
            ? {
                  key: 'node-step-down',
                  icon: <NodeCollapseOutlined />,
                  label: (
                      <NodeStepDownButton
                          node={node as CcMongoNode}
                          onSuccess={handleActionSuccess}
                      />
                  ),
              }
            : null;

    if (!node.isType(CcNodeType.PROMETHEUS)) {
        nodeSshConsoleButton = {
            key: 'node-ssh-console',
            icon: <CodeOutlined />,
            label: <NodeSshConsoleButton node={node} cluster={cluster} />,
        };
    }

    const maintenanceGroup = [
        nodeEnableMaintenanceModeButton,
        nodeManageMaintenanceModeButton,
        nodeDisableMaintenanceModeButton,
    ].filter(Boolean);

    const maintenance = groupSimilarMenuItemsInSubmenu(
        {
            key: 'maintenance',
            label: 'Maintenance',
            icon: <ToolOutlined />,
        },
        maintenanceGroup
    );

    const replicaGroup = [
        nodePromoteSlaveButton,
        nodeStartSlaveButton,
        nodeStopSlaveButton,
        nodeResetReplicaButton,
        nodeRebuildReplicationSlaveButton,
        nodeChangeReplicationMasterButton,
    ].filter(Boolean);

    const replica = groupSimilarMenuItemsInSubmenu(
        {
            key: 'replication',
            label: 'Replication',
            waitForConfirm: true,
            icon: <ForkOutlined />,
        },
        replicaGroup
    );

    const toggleItems: any = addDividerToMenuGroup(
        [
            nodeToggleSynchronousReplicationButton,
            nodeToggleReadonlyButton,
            nodeDisableBinaryLogButton,
            nodeEnableBinaryLogButton,
        ],
        true
    );

    const items = [
        nodeDetailsButton,
        maintenance,
        nodeStartButton,
        nodeStepDownButton,
        nodeFreezeButton,
        nodeProxySqlSynchronizeInstancesButton,
        nodeProxySqlExportConfigButton,
        nodeConfigureWalButton,
        replica,
        nodeSshConsoleButton,
        ...toggleItems,
        { type: 'divider', label: <AppDivider noMargin={true} /> },
        nodeResyncButton,
        bootstrapCluster,
        nodeRestartButton,
        nodeRebootHostButton,
        nodeStopButton,
        nodeUnregisterButton,
    ].filter(Boolean);
    return (
        <ActionsMenu
            disabled={items.length === 0}
            items={items as ActionMenuItem[]}
            {...rest}
        />
    );
}
